import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FileText,
  LayoutDashboard,
  Users,
  Building2,
  Calendar,
  ChevronDown,
  ChevronRight,
  LogOut,
  GitBranch,
} from "lucide-react";
import logoutUser from "../utils/logout";
import ConfirmationDialog from "../Components/shared/ConfirmationDialog";
import logo from "../Assets/1-6629bc4d.png";

const SideBar = ({
  isMobileOpen,
  setIsMobileOpen,
  isHovered,
  setIsHovered,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isReportsOpen, setIsReportsOpen] = useState(false);
  const [isUsersOpen, setIsUsersOpen] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Handle scroll lock when mobile menu is open
  useEffect(() => {
    if (isMobileOpen && isMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobileOpen, isMobile]);

  // Detect screen size to toggle mobile view
  useEffect(() => {
    const handleResize = () => {
      const isMobileView = window.innerWidth < 768;
      setIsMobile(isMobileView);
      if (!isMobileView && isMobileOpen) {
        setIsMobileOpen(false);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobileOpen, setIsMobileOpen]);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleLogout = () => {
    setShowLogoutDialog(true);
  };

  const confirmLogout = async () => {
    const success = await logoutUser();
    if (success) {
      setShowLogoutDialog(false);
      navigate("/");
    }
  };

  const cancelLogout = () => {
    setShowLogoutDialog(false);
  };

  // Get user role from localStorage
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const userRole = userDetails.role;

  // Define menu items based on role
  let menuItems = [];

  if (userRole === "hospital_admin") {
    // Hospital admin sees Departments, Doctors, and Branch Management (Users dropdown added separately below)
    menuItems = [
      { label: "Departments", icon: Building2, path: "/dashboard/department" },
      { label: "Doctors", icon: Users, path: "/dashboard/doctors" },
      {
        label: "Branch Management",
        icon: GitBranch,
        path: "/dashboard/branches",
      },
    ];
  } else {
    // Branch admin sees all menu items
    menuItems = [
      { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
      { label: "Departments", icon: Building2, path: "/dashboard/department" },
      { label: "Doctors", icon: Users, path: "/dashboard/doctors" },
      { label: "Bookings", icon: Calendar, path: "/dashboard/bookings" },
    ];
  }

  const onMobileClose = () => {
    setIsMobileOpen(false);
  };

  return (
    <>
      {/* Mobile menu overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={onMobileClose}
        ></div>
      )}

      {/* Logo Section - Separate floating box */}
      <div
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => !isMobile && setIsHovered(false)}
        className={`hidden md:flex fixed top-4 left-4 rounded-[22px] z-50 transition-all duration-300 ease-in-out overflow-hidden ${
          isHovered ? "w-64 px-6 py-4" : "w-20 h-20 items-center justify-center"
        }`}
        style={{
          backdropFilter: "blur(60px) saturate(200%) brightness(0.85)",
          WebkitBackdropFilter: "blur(60px) saturate(200%) brightness(0.85)",
          backgroundColor: "rgba(0, 0, 0, 0.15)",
          boxShadow:
            "0 8px 32px 0 rgba(0, 0, 0, 0.3), 0 1px 0 0 rgba(255, 255, 255, 0.3) inset, 0 -1px 0 0 rgba(0, 0, 0, 0.15) inset",
          border: "0.5px solid rgba(255, 255, 255, 0.3)",
        }}
      >
        {!isHovered ? (
          <img
            src={logo}
            alt="FinGlider Logo"
            className="w-10 h-10 rounded-lg flex-shrink-0 drop-shadow-[0_4px_8px_rgba(0,0,0,0.3)]"
          />
        ) : (
          <div className="flex items-center gap-3 w-full">
            <img
              src={logo}
              alt="FinGlider Logo"
              className="w-10 h-10 rounded-lg flex-shrink-0 drop-shadow-[0_4px_8px_rgba(0,0,0,0.3)]"
            />
            <div className="flex flex-col min-w-0 flex-1">
              <h2 className="text-xl font-bold tracking-wide text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.6)]">
                FinGlider
              </h2>
              <p className="text-xs font-medium text-white/90 break-words leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
                Welcome to Appointment System
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Menu - Separate floating box */}
      <div
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => !isMobile && setIsHovered(false)}
        className={`fixed rounded-[22px] z-50 flex flex-col transition-all duration-300 ease-in-out overflow-hidden ${
          isMobile
            ? `top-0 left-0 w-64 h-screen transform ${
                isMobileOpen ? "translate-x-0" : "-translate-x-full"
              }`
            : isHovered
            ? "top-28 left-4 w-64 h-[calc(100vh-9rem)]"
            : "top-28 left-4 w-20 h-[calc(100vh-9rem)]"
        }`}
        style={{
          backdropFilter: "blur(60px) saturate(200%) brightness(0.85)",
          WebkitBackdropFilter: "blur(60px) saturate(200%) brightness(0.85)",
          backgroundColor: "rgba(0, 0, 0, 0.15)",
          boxShadow:
            "0 8px 32px 0 rgba(0, 0, 0, 0.3), 0 1px 0 0 rgba(255, 255, 255, 0.3) inset, 0 -1px 0 0 rgba(0, 0, 0, 0.15) inset",
          border: "0.5px solid rgba(255, 255, 255, 0.3)",
        }}
      >
        {/* Mobile Header */}
        {isMobile && (
          <div className="border-b border-white/10 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img
                  src={logo}
                  alt="FinGlider Logo"
                  className="w-10 h-10 rounded-lg flex-shrink-0"
                />
                <h2 className="text-xl font-bold tracking-wide text-white whitespace-nowrap">
                  FinGlider
                </h2>
              </div>
              <button
                onClick={onMobileClose}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                aria-label="Close menu"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto overscroll-contain min-h-0 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent p-3">
          <ul className="space-y-1">
            {menuItems.map(({ label, icon: Icon, path }) => (
              <li key={path}>
                <Link
                  to={path}
                  onClick={isMobile ? onMobileClose : undefined}
                  className={`
                    flex items-center rounded-xl transition-all duration-200 overflow-hidden group
                    ${
                      !isHovered && !isMobile
                        ? "justify-center p-3"
                        : "gap-3 px-4 py-3"
                    }
                    ${
                      isActive(path)
                        ? "bg-white/90 text-blue-600 shadow-lg backdrop-blur-sm"
                        : "hover:bg-white/10 text-white"
                    }
                  `}
                  title={!isHovered && !isMobile ? label : ""}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span
                    className={`font-medium whitespace-nowrap transition-all duration-300 ${
                      isHovered || isMobile
                        ? "opacity-100 max-w-full"
                        : "opacity-0 max-w-0"
                    }`}
                  >
                    {label}
                  </span>
                </Link>
              </li>
            ))}

            {/* Users Dropdown - For hospital_admin and branch_admin */}
            {(userRole === "hospital_admin" || userRole === "branch_admin") && (
              <li>
                <button
                  onClick={() => setIsUsersOpen(!isUsersOpen)}
                  className={`w-full flex items-center overflow-hidden rounded-xl transition-all duration-200 hover:bg-white/10 focus:outline-none ${
                    !isHovered && !isMobile
                      ? "justify-center p-3"
                      : "justify-between px-4 py-3"
                  } ${
                    isActive("/dashboard/users/branch-admins") ||
                    isActive("/dashboard/users/nurses")
                      ? "bg-white/90 text-blue-600 shadow-lg backdrop-blur-sm"
                      : "text-white"
                  }`}
                  title={!isHovered && !isMobile ? "Users" : ""}
                >
                  <div
                    className={`flex items-center overflow-hidden ${
                      !isHovered && !isMobile ? "" : "gap-3"
                    }`}
                  >
                    <Users className="w-5 h-5 flex-shrink-0" />
                    <span
                      className={`font-medium whitespace-nowrap transition-all duration-300 ${
                        isHovered || isMobile
                          ? "opacity-100 max-w-full"
                          : "opacity-0 max-w-0"
                      }`}
                    >
                      Users
                    </span>
                  </div>
                  <div
                    className={`transition-all duration-300 ${
                      isHovered || isMobile
                        ? "opacity-100 w-4"
                        : "opacity-0 w-0"
                    }`}
                  >
                    {isUsersOpen ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isUsersOpen && (isHovered || isMobile)
                      ? "max-h-96 opacity-100"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  <ul className="mt-2 pl-8 space-y-1">
                    {/* Branch Admins - Only for hospital_admin */}
                    {userRole === "hospital_admin" && (
                      <li>
                        <Link
                          to="/dashboard/users/branch-admins"
                          onClick={isMobile ? onMobileClose : undefined}
                          className={`block px-4 py-2 rounded-xl text-sm transition-all duration-200 hover:bg-white/10 focus:outline-none ${
                            isActive("/dashboard/users/branch-admins")
                              ? "bg-white/90 backdrop-blur-sm text-blue-600 shadow-lg"
                              : "text-white"
                          }`}
                        >
                          Branch Admins
                        </Link>
                      </li>
                    )}
                    {/* Nurses - For both hospital_admin and branch_admin */}
                    <li>
                      <Link
                        to="/dashboard/users/nurses"
                        onClick={isMobile ? onMobileClose : undefined}
                        className={`block px-4 py-2 rounded-xl text-sm transition-all duration-200 hover:bg-white/10 focus:outline-none ${
                          isActive("/dashboard/users/nurses")
                            ? "bg-white/90 backdrop-blur-sm text-blue-600 shadow-lg"
                            : "text-white"
                        }`}
                      >
                        Nurses
                      </Link>
                    </li>
                    {/* Nurse Assignments - Only for branch_admin */}
                    {userRole === "branch_admin" && (
                      <li>
                        <Link
                          to="/dashboard/nurse-assignments"
                          onClick={isMobile ? onMobileClose : undefined}
                          className={`block px-4 py-2 rounded-xl text-sm transition-all duration-200 hover:bg-white/10 focus:outline-none ${
                            isActive("/dashboard/nurse-assignments")
                              ? "bg-white/90 backdrop-blur-sm text-blue-600 shadow-lg"
                              : "text-white"
                          }`}
                        >
                          Nurse Assignments
                        </Link>
                      </li>
                    )}
                  </ul>
                </div>
              </li>
            )}

            {/* Reports Dropdown - Show for branch_admin and hospital_admin */}
            {(userRole === "branch_admin" || userRole === "hospital_admin") && (
              <li>
                <button
                  onClick={() => setIsReportsOpen(!isReportsOpen)}
                  className={`w-full flex items-center overflow-hidden rounded-xl transition-all duration-200 hover:bg-white/10 focus:outline-none ${
                    !isHovered && !isMobile
                      ? "justify-center p-3"
                      : "justify-between px-4 py-3"
                  } ${
                    isActive("/dashboard/reports")
                      ? "bg-white/90 backdrop-blur-sm text-blue-600 shadow-lg"
                      : "text-white"
                  }`}
                  title={!isHovered && !isMobile ? "Reports" : ""}
                >
                  <div
                    className={`flex items-center overflow-hidden ${
                      !isHovered && !isMobile ? "" : "gap-3"
                    }`}
                  >
                    <FileText className="w-5 h-5 flex-shrink-0" />
                    <span
                      className={`font-medium whitespace-nowrap transition-all duration-300 ${
                        isHovered || isMobile
                          ? "opacity-100 max-w-full"
                          : "opacity-0 max-w-0"
                      }`}
                    >
                      Reports
                    </span>
                  </div>
                  <div
                    className={`transition-all duration-300 ${
                      isHovered || isMobile
                        ? "opacity-100 w-4"
                        : "opacity-0 w-0"
                    }`}
                  >
                    {isReportsOpen ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isReportsOpen && (isHovered || isMobile)
                      ? "max-h-96 opacity-100"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  <ul className="mt-2 pl-8 space-y-1">
                    <li>
                      <Link
                        to="/dashboard/reports/bookings"
                        onClick={isMobile ? onMobileClose : undefined}
                        className={`block px-4 py-2 rounded-xl text-sm transition-all duration-200 hover:bg-white/10 focus:outline-none ${
                          isActive("/dashboard/reports/bookings")
                            ? "bg-white/90 backdrop-blur-sm text-blue-600 shadow-lg"
                            : "text-white"
                        }`}
                      >
                        Booking Analytics
                      </Link>
                    </li>
                  </ul>
                </div>
              </li>
            )}

            {/* Logout Button */}
            <li>
              <button
                onClick={handleLogout}
                className={`w-full flex items-center rounded-xl text-white hover:bg-white/10 transition-all duration-200 overflow-hidden ${
                  !isHovered && !isMobile
                    ? "justify-center p-3"
                    : "gap-3 px-4 py-3"
                }`}
                title={!isHovered && !isMobile ? "Logout" : ""}
              >
                <LogOut className="w-5 h-5 flex-shrink-0" />
                <span
                  className={`font-medium whitespace-nowrap transition-all duration-300 ${
                    isHovered || isMobile
                      ? "opacity-100 max-w-full"
                      : "opacity-0 max-w-0"
                  }`}
                >
                  Logout
                </span>
              </button>
            </li>
          </ul>
        </nav>

        {/* Footer */}
        <div
          className={`mt-auto p-4 border-t border-white/10 transition-all duration-300 overflow-hidden ${
            isHovered || isMobile ? "max-h-20 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <p className="text-sm text-gray-400 text-center whitespace-nowrap">
            © 2025 Fin Glider
          </p>
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showLogoutDialog}
        onClose={cancelLogout}
        onConfirm={confirmLogout}
        title="Confirm Logout"
        message="Are you sure you want to logout?"
      />
    </>
  );
};

export default SideBar;
